/**
 * Two questions about the machine the portal is running on: is this an Apple mobile browser, and is
 * the portal running as an installed application rather than as a tab.
 *
 * They live here rather than inside the panel that asks them because both answers decide what a
 * customer is told, and a sentence shown to a customer has to be testable. `detectPlatform` takes
 * every value it needs as an argument for exactly that reason: `navigator.standalone` exists only in
 * Safari, `matchMedia` is missing in a non-browser runtime and throws on an unknown query in some
 * older ones, and the user-agent string is not present at all in a Worker.
 *
 * **What a user-agent test can know and what it cannot.** It can tell that a browser claims to be
 * Mobile Safari on an iPhone, and in practice every browser on iOS is WebKit underneath, so the
 * claim is a reliable guide to the platform's install and push behaviour. It cannot tell the iOS
 * version reliably: Safari freezes and rounds parts of the string, an iPad has reported itself as a
 * desktop Mac since iPadOS 13, and any browser can be told to lie by its own settings. So this
 * module answers "which platform" and never "which version". Anything that depends on a version is
 * decided by asking the runtime whether the capability is actually there, which is a fact rather
 * than a claim. `app/pwa/push.ts` does exactly that.
 */

export interface PlatformFacts {
  /** An iPhone, an iPod or an iPad, including an iPad that presents itself as a Mac. */
  isIos: boolean;
  /** Launched from the home screen or an installed window rather than opened in a browser tab. */
  isStandalone: boolean;
  /**
   * A computer rather than a phone or a tablet. Added 2026-09-21 for one sentence in Den här
   * enheten: on a computer a push arrives only while the browser is running, which is how Chrome on
   * Windows behaved when it was measured that day, and a person who closed every window reads the
   * silence as the portal being broken. A phone's operating system wakes the browser for a push,
   * so the sentence would be wrong there.
   */
  isDesktop: boolean;
}

export interface PlatformInput {
  userAgent: string;
  /**
   * `navigator.maxTouchPoints`. The only thing separating an iPad from a MacBook in a user-agent
   * string since iPadOS 13, which reports `Macintosh; Intel Mac OS X 10_15_7` character for
   * character. A Mac with a touch display would be a false positive here; Apple does not sell one.
   */
  maxTouchPoints: number;
  /** Safari's own installed flag. Absent everywhere else, which is why it is optional. */
  standalone?: boolean;
  matchMedia?: (query: string) => { matches: boolean };
}

const STANDALONE_QUERY = "(display-mode: standalone)";

export function detectPlatform(input: PlatformInput): PlatformFacts {
  const agent = input.userAgent ?? "";

  const saysApple = /iPhone|iPad|iPod/.test(agent);
  const iPadAsMac = /Macintosh/.test(agent) && input.maxTouchPoints > 1;

  let displayModeStandalone = false;

  // A query that cannot be asked is not a yes. jsdom answers `false` for everything, an old runtime
  // may throw on a media feature it does not know, and neither of those means the portal was
  // installed.
  if (typeof input.matchMedia === "function") {
    try {
      displayModeStandalone = input.matchMedia(STANDALONE_QUERY).matches === true;
    } catch {
      displayModeStandalone = false;
    }
  }

  // Android names itself, and every other phone or tablet browser carries `Mobi` in its string.
  // What is left is a computer. An iPad presenting as a Mac is caught by `iPadAsMac` first.
  const isMobile = saysApple || iPadAsMac || /Android|Mobi/.test(agent);

  return {
    isDesktop: !isMobile,
    isIos: saysApple || iPadAsMac,
    // Safari answers the first and not the second. Everything else answers the second and not the
    // first. Asking both is the only way to get a yes on both families.
    isStandalone: input.standalone === true || displayModeStandalone
  };
}

/**
 * The same question asked of the real browser, with every value guarded.
 *
 * A separate function from the pure one so that nothing which reads the environment is ever in the
 * path of a test, and so that a runtime with no `navigator` at all answers "not iOS, not installed"
 * instead of throwing on the first property access.
 */
export function readPlatform(): PlatformFacts {
  if (typeof navigator === "undefined") {
    // No browser, so no computer either: the sentence it gates is about a browser that is running.
    return { isIos: false, isStandalone: false, isDesktop: false };
  }

  return detectPlatform({
    userAgent: navigator.userAgent ?? "",
    maxTouchPoints: navigator.maxTouchPoints ?? 0,
    // Non-standard and Safari-only, so it is read off a widened type rather than declared globally.
    standalone: (navigator as Navigator & { standalone?: boolean }).standalone === true,
    matchMedia:
      typeof window !== "undefined" && typeof window.matchMedia === "function"
        ? (query: string) => window.matchMedia(query)
        : undefined
  });
}
